(function () {
  'use strict';

  var config = window.HERMES_BEHAVIOR_ANALYTICS || {};
  var pageStartedAt = Date.now();
  var seenSections = new WeakSet();
  var seenCtas = new WeakSet();
  var sectionTimers = new WeakMap();
  var ctaTimers = new WeakMap();
  var lastClickByKey = {};
  var touchStartByElement = new WeakMap();
  var CLICK_COOLDOWN_MS = 2000;
  var SECTION_MIN_VISIBLE_MS = 2000;
  var CTA_MIN_VISIBLE_MS = 1000;
  var reportedViewport = false;

  function stripQuery(path) {
    return String(path || window.location.pathname || '').split('?')[0].split('#')[0].slice(0, 180);
  }

  function cleanText(value, limit) {
    var text = String(value || '').replace(/\s+/g, ' ').trim();
    text = text.replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[redacted]');
    text = text.replace(/\+?\d[\d\s().-]{6,}\d/g, '[redacted]');
    return text.slice(0, limit || 80);
  }

  function cleanHandle(value) {
    return String(value || '').toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 120) || undefined;
  }

  function cleanId(value) {
    var text = String(value || '').trim();
    return /^\d{5,}$/.test(text) ? text.slice(0, 40) : undefined;
  }

  function shortHash(value) {
    var text = String(value || '').slice(0, 300);
    var hash = 0;
    for (var i = 0; i < text.length; i += 1) {
      hash = ((hash << 5) - hash + text.charCodeAt(i)) | 0;
    }
    return Math.abs(hash).toString(36).slice(0, 12);
  }

  function numberValue(value) {
    var parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  function bucketNumber(value, buckets) {
    var number = numberValue(value);
    if (number === undefined) return undefined;
    for (var i = 0; i < buckets.length; i += 1) {
      if (number < buckets[i][0]) return buckets[i][1];
    }
    return buckets.length ? buckets[buckets.length - 1][2] : undefined;
  }

  function cartValueBucket(value) {
    var number = numberValue(value);
    if (number === undefined) return undefined;
    if (number < 50) return '0-50';
    if (number < 100) return '50-100';
    if (number < 200) return '100-200';
    return '200+';
  }

  function viewportBucket(width) {
    var value = numberValue(width || window.innerWidth);
    if (value === undefined) return undefined;
    if (value < 360) return 'xs';
    if (value < 390) return 'sm';
    if (value < 430) return 'md';
    if (value < 768) return 'lg_phone';
    if (value < 1024) return 'tablet';
    return 'desktop';
  }

  function positionBucket(value) {
    var number = numberValue(value);
    if (number === undefined) return undefined;
    if (number <= 4) return '1-4';
    if (number <= 8) return '5-8';
    if (number <= 12) return '9-12';
    return '13+';
  }

  function safeAreaBucket() {
    var width = window.innerWidth || 0;
    var height = window.innerHeight || 0;
    if (width < 390 && height < 700) return 'compact';
    if (width < 768) return 'mobile_regular';
    return 'wide';
  }

  function baseParams() {
    return {
      page_type: cleanText(config.page_type || document.body.className.match(/template-([a-z0-9-]+)/i)?.[1] || 'unknown', 40),
      page_path: stripQuery(config.page_path),
      locale: cleanText(config.locale || document.documentElement.lang || '', 20)
    };
  }

  function sendEvent(name, params) {
    try {
      var payload = Object.assign(baseParams(), params || {});
      Object.keys(payload).forEach(function (key) {
        if (payload[key] === undefined || payload[key] === null || payload[key] === '') {
          delete payload[key];
        }
      });
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, payload);
      } else if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: name }, payload));
      }
    } catch (error) {
      if (config.debug) {
        console.warn('[Hermes behavior analytics] event skipped', error);
      }
    }
  }

  function reportMobileViewport() {
    if (reportedViewport) return;
    reportedViewport = true;
    var width = window.innerWidth || document.documentElement.clientWidth || 0;
    var height = window.innerHeight || document.documentElement.clientHeight || 0;
    sendEvent('hermes_mobile_viewport', {
      viewport_width: width,
      viewport_height: height,
      screen_bucket: viewportBucket(width),
      is_mobile: width < 768 ? 'true' : 'false',
      is_touch: ('ontouchstart' in window || navigator.maxTouchPoints > 0) ? 'true' : 'false',
      safe_area_bucket: safeAreaBucket()
    });
  }

  function elementRole(element) {
    if (!element) return 'unknown';
    if (element.matches('button, [type="submit"], [name="add"], [data-pf-type*="ATC" i]')) return 'button';
    if (element.matches('a[href]')) return 'link';
    if (element.matches('img, picture, video, [data-pf-type*="Media" i], .product-gallery, .product-media')) return 'media';
    if (element.matches('select, input[type="radio"], input[type="checkbox"], [data-option-position], [data-option-value]')) return 'option';
    return cleanText(element.getAttribute('data-pf-type') || element.tagName || 'element', 40).toLowerCase();
  }

  function sectionInfo(element) {
    var section = element && element.closest('[data-section-id], .shopify-section, section[id], [id^="shopify-section-"]');
    if (!section) {
      return { section_id: 'page', section_name: 'page' };
    }
    var rawId = section.getAttribute('data-section-id') || section.id || 'section';
    var sectionId = cleanText(rawId.replace(/^shopify-section-/, ''), 80);
    var heading = section.querySelector('h1, h2, h3, [data-pf-type*="Heading" i], .section__header');
    return {
      section_id: sectionId,
      section_name: cleanText(section.getAttribute('data-section-type') || section.getAttribute('data-pf-type') || (heading && heading.textContent) || sectionId, 80)
    };
  }

  function productHandleFromUrl(url) {
    var match = String(url || '').match(/\/products\/([^/?#]+)/i);
    return match ? cleanHandle(match[1]) : undefined;
  }

  function productHandleFromPage() {
    var canonical = document.querySelector('link[rel="canonical"]');
    return cleanHandle(config.product_handle) ||
      productHandleFromUrl(canonical && canonical.href) ||
      productHandleFromUrl(window.location.pathname);
  }

  function closestProductHandle(element) {
    var holder = element && element.closest([
      '[data-product-handle]',
      '[data-handle]',
      '[data-product-url]',
      '[data-url]',
      '[href*="/products/"]',
      'product-form',
      'form[action*="/cart/add"]',
      '.product',
      '.product-item',
      '.product-card',
      '.card-wrapper',
      '.grid__item',
      '[data-pf-type*="Product" i]'
    ].join(', '));
    if (!holder) return undefined;
    var directHandle = cleanHandle(holder.getAttribute('data-product-handle') || holder.getAttribute('data-handle'));
    if (directHandle) return directHandle;
    var directUrl = holder.href || holder.getAttribute('data-product-url') || holder.getAttribute('data-url') || holder.getAttribute('action');
    var fromDirectUrl = productHandleFromUrl(directUrl);
    if (fromDirectUrl) return fromDirectUrl;
    var nestedLink = holder.querySelector && holder.querySelector('a[href*="/products/"]');
    var fromNestedLink = productHandleFromUrl(nestedLink && nestedLink.href);
    if (fromNestedLink) return fromNestedLink;
    var form = holder.querySelector && holder.querySelector('form[action*="/cart/add"]');
    return productHandleFromUrl(form && form.getAttribute('action'));
  }

  function productInfo(element) {
    var holder = element && element.closest('[data-product-id], [data-product-handle], product-form, form[action*="/cart/add"], .product, .product-item, .product-card, .card-wrapper, .grid__item, [data-pf-type*="Product" i]');
    var link = element && (element.closest('a[href*="/products/"]') || (holder && holder.querySelector && holder.querySelector('a[href*="/products/"]')));
    var productId = holder && holder.getAttribute('data-product-id');
    return {
      product_handle: productHandleFromUrl(link && link.href) || closestProductHandle(element) || productHandleFromPage(),
      product_id: productId || config.product_id || undefined,
      variant_id: variantId(element)
    };
  }

  function variantId(element) {
    var form = element && element.closest('form[action*="/cart/add"]');
    var selected = (form && form.querySelector('[name="id"]')) || document.querySelector('form[action*="/cart/add"] [name="id"]');
    var fromElement = element && element.getAttribute('data-variant-id');
    return cleanId(fromElement) || cleanId(selected && selected.value) || cleanId(config.variant_id);
  }

  function optionValue(element) {
    if (!element || !element.matches('select, input[type="radio"], input[type="checkbox"], [data-option-position], [data-option-value]')) {
      return undefined;
    }
    return cleanText(element.getAttribute('data-option-value') || element.value || element.textContent || '', 60) || undefined;
  }

  function positionInSection(element, section) {
    var productCards = Array.prototype.slice.call(section.querySelectorAll('a[href*="/products/"], [data-product-id], [data-product-handle], .product-item, .product-card, .card-wrapper, .grid__item'));
    var card = element.closest('a[href*="/products/"], [data-product-id], [data-product-handle], .product-item, .product-card, .card-wrapper, .grid__item');
    var index = productCards.indexOf(card);
    return index >= 0 ? index + 1 : undefined;
  }

  function classifyProductAction(element) {
    var text = cleanText(element && element.textContent, 80).toLowerCase();
    var name = cleanText(element && (element.name || element.getAttribute('aria-label') || element.getAttribute('data-pf-type')), 80).toLowerCase();
    if (element && element.matches('[name="add"], [data-pf-type*="ATC" i], button[type="submit"]')) return 'add_to_cart_click';
    if (element && element.closest('.shopify-payment-button')) return 'buy_now_click';
    if (element && element.matches('select, input[type="radio"], [data-option-position], [data-option-value]')) return 'variant_select';
    if (element && element.matches('input, textarea') && /engraving|custom|message|name|text|刻字|定制/.test(text + ' ' + name)) return 'custom_option_focus';
    if (element && (element.matches('[disabled], .disabled, [aria-disabled="true"]') || /sold out|unavailable|缺货|售罄|不可用/.test(text + ' ' + name))) return 'variant_unavailable_click';
    if (/size|sizing|尺寸|尺码/.test(text + ' ' + name)) return 'size_guide_open';
    if (/shipping|delivery|物流|配送|运费/.test(text + ' ' + name)) return 'shipping_info_open';
    if (/return|refund|exchange|退换|退款|退货/.test(text + ' ' + name)) return 'returns_info_open';
    if (/material|metal|leather|stainless|材质|材料|皮革|不锈钢/.test(text + ' ' + name)) return 'material_info_open';
    if (element && elementRole(element) === 'media') return 'media_click';
    return undefined;
  }

  function ctaType(element) {
    if (!element) return undefined;
    var text = cleanText(element.textContent || element.getAttribute('aria-label') || element.name || '', 80).toLowerCase();
    var className = cleanText(element.className || '', 120).toLowerCase();
    if (element.closest('.shopify-payment-button') || /buy now|paypal|shop pay|立即购买/.test(text)) return 'buy_now';
    if (/checkout|结账|check out/.test(text + ' ' + className)) return 'checkout';
    if (/sticky|fixed|floating/.test(className)) return 'sticky_atc';
    if (element.matches('[name="add"], [data-pf-type*="ATC" i], button[type="submit"]') || /add to cart|加入购物车|添加到购物车/.test(text)) return 'add_to_cart';
    return undefined;
  }

  function sendProductDecision(element, action, extra) {
    if (!action) return;
    sendEvent('hermes_product_decision', Object.assign({}, productInfo(element), {
      action: action,
      option_value: action === 'variant_select' ? optionValue(element) : undefined
    }, extra || {}));
  }

  function sendCartFriction(action, extra) {
    sendEvent('hermes_cart_friction', Object.assign({
      action: action,
      cart_item_count: numberValue(config.cart_item_count),
      cart_value_bucket: cartValueBucket(config.cart_value)
    }, extra || {}));
  }

  function handleClick(event) {
    try {
      var target = event.target && event.target.closest('a, button, input, select, [role="button"], [data-pf-type], img, video');
      if (!target) return;

      var section = target.closest('[data-section-id], .shopify-section, section[id], [id^="shopify-section-"]') || document.body;
      var info = sectionInfo(target);
      var role = elementRole(target);
      var product = productInfo(target);
      var clickKey = [info.section_id, role, product.product_handle || '', cleanText(target.textContent || target.getAttribute('aria-label') || target.href || '', 50)].join('|');
      var now = Date.now();
      if (lastClickByKey[clickKey] && now - lastClickByKey[clickKey] < CLICK_COOLDOWN_MS) return;
      lastClickByKey[clickKey] = now;

      var sectionParams = Object.assign({}, info, product, {
        element_role: role,
        element_text: cleanText(target.textContent || target.getAttribute('aria-label') || target.getAttribute('title') || '', 80),
        target_url: target.href ? stripQuery(new URL(target.href, window.location.origin).pathname) : undefined,
        position: positionInSection(target, section)
      });
      sendEvent('hermes_section_click', sectionParams);

      var productAction = classifyProductAction(target);
      if (productAction || product.product_handle || config.page_type === 'product') {
        sendEvent('hermes_product_action', Object.assign({}, product, {
          action: productAction || 'product_click',
          option_value: productAction === 'variant_select' ? optionValue(target) : undefined
        }));
      }
      if (productAction) {
        sendProductDecision(target, productAction);
      }

      var href = target.href || '';
      if (/\/cart($|[/?#])/.test(href) || /\/checkout($|[/?#])/.test(href) || /checkout/i.test(target.name || target.textContent || '')) {
        var cartAction = /checkout/i.test(href + ' ' + (target.name || target.textContent || '')) ? 'checkout_click' : 'cart_open';
        sendEvent('hermes_cart_action', {
          action: cartAction,
          cart_item_count: numberValue(config.cart_item_count),
          cart_value: numberValue(config.cart_value)
        });
        sendCartFriction(cartAction);
      }

      if (target.matches('input[name*="discount" i], input[id*="discount" i], input[name*="coupon" i], input[id*="coupon" i]')) {
        sendCartFriction('discount_focus');
      }
      if (/shipping|delivery|物流|配送|运费/.test(cleanText(target.textContent || target.getAttribute('aria-label') || '', 80).toLowerCase())) {
        sendCartFriction('shipping_info_open');
      }
    } catch (error) {
      if (config.debug) {
        console.warn('[Hermes behavior analytics] click skipped', error);
      }
    }
  }

  function handleChange(event) {
    try {
      var target = event.target;
      if (!target) return;
      if (target.matches('select, input[type="radio"], [data-option-position], [data-option-value]')) {
        sendProductDecision(target, 'variant_select');
      }
      if (target.matches('input[type="number"][name*="quantity" i], input[name="quantity"], select[name*="quantity" i]')) {
        sendCartFriction('quantity_change');
      }
    } catch (error) {
      if (config.debug) console.warn('[Hermes behavior analytics] change skipped', error);
    }
  }

  function handleFocusIn(event) {
    try {
      var target = event.target;
      if (!target || !target.matches('input, textarea')) return;
      var name = cleanText(target.name || target.id || target.getAttribute('aria-label') || '', 80).toLowerCase();
      if (/engraving|custom|message|name|text|刻字|定制/.test(name)) {
        sendProductDecision(target, 'custom_option_focus');
      }
      if (/discount|coupon|折扣|优惠/.test(name)) {
        sendCartFriction('discount_focus');
      }
    } catch (error) {
      if (config.debug) console.warn('[Hermes behavior analytics] focus skipped', error);
    }
  }

  function handleTouchStart(event) {
    var target = event.target && event.target.closest('img, picture, video, [data-pf-type*="Media" i], .product-gallery, .product-media');
    if (!target || !event.changedTouches || !event.changedTouches[0]) return;
    touchStartByElement.set(target, {
      x: event.changedTouches[0].clientX,
      y: event.changedTouches[0].clientY,
      at: Date.now()
    });
  }

  function handleTouchEnd(event) {
    try {
      var target = event.target && event.target.closest('img, picture, video, [data-pf-type*="Media" i], .product-gallery, .product-media');
      if (!target || !event.changedTouches || !event.changedTouches[0]) return;
      var started = touchStartByElement.get(target);
      if (!started) return;
      var dx = event.changedTouches[0].clientX - started.x;
      var dy = event.changedTouches[0].clientY - started.y;
      if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy) && Date.now() - started.at < 1200) {
        sendProductDecision(target, 'media_swipe');
      }
    } catch (error) {
      if (config.debug) console.warn('[Hermes behavior analytics] touch skipped', error);
    }
  }

  function observeSections() {
    if (!('IntersectionObserver' in window)) return;
    var candidates = Array.prototype.slice.call(document.querySelectorAll('#main > .shopify-section, #main section, [data-section-id]'));
    if (!candidates.length) candidates = [document.querySelector('#main') || document.body];
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var element = entry.target;
        if (seenSections.has(element)) return;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
          if (!sectionTimers.has(element)) {
            sectionTimers.set(element, {
              started_at: Date.now(),
              max_ratio: entry.intersectionRatio
            });
          } else {
            sectionTimers.get(element).max_ratio = Math.max(sectionTimers.get(element).max_ratio, entry.intersectionRatio);
          }
          window.setTimeout(function () {
            var timer = sectionTimers.get(element);
            if (!timer || seenSections.has(element)) return;
            var visibleMs = Date.now() - timer.started_at;
            if (visibleMs >= SECTION_MIN_VISIBLE_MS) {
              seenSections.add(element);
              sendEvent('hermes_section_view', Object.assign(sectionInfo(element), {
                visible_ms: visibleMs,
                max_visible_ratio: Math.round((timer.max_ratio || entry.intersectionRatio || 0) * 100) / 100
              }));
              observer.unobserve(element);
            }
          }, SECTION_MIN_VISIBLE_MS);
        } else {
          sectionTimers.delete(element);
        }
      });
    }, { threshold: [0.35, 0.6, 0.9] });
    candidates.forEach(function (section) {
      if (section) observer.observe(section);
    });
  }

  function observeCtas() {
    if (!('IntersectionObserver' in window)) return;
    var candidates = Array.prototype.slice.call(document.querySelectorAll(
      '[name="add"], [data-pf-type*="ATC" i], button[type="submit"], .shopify-payment-button button, a[href*="/checkout"], button[name*="checkout" i], [class*="sticky" i] button'
    )).filter(function (element) {
      return ctaType(element);
    });
    if (!candidates.length) return;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var element = entry.target;
        if (seenCtas.has(element)) return;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          if (!ctaTimers.has(element)) {
            ctaTimers.set(element, { started_at: Date.now() });
          }
          window.setTimeout(function () {
            var timer = ctaTimers.get(element);
            if (!timer || seenCtas.has(element)) return;
            var visibleMs = Date.now() - timer.started_at;
            if (visibleMs >= CTA_MIN_VISIBLE_MS) {
              seenCtas.add(element);
              var type = ctaType(element);
              sendEvent('hermes_cta_exposure', Object.assign({}, productInfo(element), {
                action: type,
                cta_type: type,
                visible_ms: visibleMs,
                position_bucket: positionBucket(positionInSection(element, element.closest('[data-section-id], .shopify-section, section[id], [id^="shopify-section-"]') || document.body))
              }));
              observer.unobserve(element);
            }
          }, CTA_MIN_VISIBLE_MS);
        } else {
          ctaTimers.delete(element);
        }
      });
    }, { threshold: [0.5, 0.8] });
    candidates.forEach(function (element) {
      observer.observe(element);
    });
  }

  function performanceBucket(metric, value) {
    var number = numberValue(value);
    if (number === undefined) return undefined;
    if (metric === 'lcp') return number <= 2500 ? 'good' : (number <= 4000 ? 'needs_improvement' : 'poor');
    if (metric === 'cls') return number <= 0.1 ? 'good' : (number <= 0.25 ? 'needs_improvement' : 'poor');
    if (metric === 'inp') return number <= 200 ? 'good' : (number <= 500 ? 'needs_improvement' : 'poor');
    return undefined;
  }

  function sendUxQuality(metric, value) {
    var bucket = performanceBucket(metric, value);
    if (!bucket) return;
    sendEvent('hermes_ux_quality', {
      action: metric,
      metric: metric,
      value_bucket: bucket
    });
  }

  function observeUxQuality() {
    if (!('PerformanceObserver' in window)) return;
    try {
      new PerformanceObserver(function (list) {
        var entries = list.getEntries();
        var last = entries[entries.length - 1];
        if (last) sendUxQuality('lcp', last.startTime);
      }).observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (error) {}
    try {
      new PerformanceObserver(function (list) {
        var cls = 0;
        list.getEntries().forEach(function (entry) {
          if (!entry.hadRecentInput) cls += entry.value || 0;
        });
        if (cls) sendUxQuality('cls', cls);
      }).observe({ type: 'layout-shift', buffered: true });
    } catch (error) {}
    try {
      new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (entry) {
          sendUxQuality('inp', entry.duration || entry.processingEnd - entry.startTime);
        });
      }).observe({ type: 'event', buffered: true, durationThreshold: 104 });
    } catch (error) {}
  }

  function observeAddToCartResult() {
    if (window.fetch) {
      var originalFetch = window.fetch;
      window.fetch = function () {
        var args = arguments;
        var url = String((args[0] && args[0].url) || args[0] || '');
        var isCartAdd = /\/cart\/add(\.js)?($|[?#])/.test(url);
        return originalFetch.apply(this, args).then(function (response) {
          if (isCartAdd) {
            sendEvent('hermes_ux_quality', {
              action: response && response.ok ? 'add_to_cart_success' : 'add_to_cart_failed',
              metric: response && response.ok ? 'add_to_cart_success' : 'add_to_cart_failed',
              value_bucket: response && response.ok ? 'ok' : 'failed'
            });
          }
          return response;
        }).catch(function (error) {
          if (isCartAdd) {
            sendEvent('hermes_ux_quality', {
              action: 'add_to_cart_failed',
              metric: 'add_to_cart_failed',
              value_bucket: 'failed'
            });
          }
          throw error;
        });
      };
    }
    if (window.XMLHttpRequest && window.XMLHttpRequest.prototype) {
      var originalOpen = window.XMLHttpRequest.prototype.open;
      var originalSend = window.XMLHttpRequest.prototype.send;
      window.XMLHttpRequest.prototype.open = function (method, url) {
        this.__hermesCartAdd = /\/cart\/add(\.js)?($|[?#])/.test(String(url || ''));
        return originalOpen.apply(this, arguments);
      };
      window.XMLHttpRequest.prototype.send = function () {
        if (this.__hermesCartAdd) {
          this.addEventListener('loadend', function () {
            sendEvent('hermes_ux_quality', {
              action: this.status >= 200 && this.status < 300 ? 'add_to_cart_success' : 'add_to_cart_failed',
              metric: this.status >= 200 && this.status < 300 ? 'add_to_cart_success' : 'add_to_cart_failed',
              value_bucket: this.status >= 200 && this.status < 300 ? 'ok' : 'failed'
            });
          });
        }
        return originalSend.apply(this, arguments);
      };
    }
  }

  function errorSourceBucket(filename) {
    var source = String(filename || '');
    if (!source) return 'inline_or_unknown';
    if (source.indexOf(window.location.hostname) >= 0 || source.charAt(0) === '/') return 'theme';
    if (/cdn\.shopify|shopifycdn|myshopify/.test(source)) return 'shopify_cdn';
    if (/googletagmanager|google-analytics|googleadservices|google/.test(source)) return 'google';
    if (/pagefly/i.test(source)) return 'pagefly';
    return 'third_party';
  }

  function observeJsErrors() {
    window.addEventListener('error', function (event) {
      var message = cleanText((event && event.message) || 'script_error', 120);
      var sourceBucket = errorSourceBucket(event && event.filename);
      sendEvent('hermes_ux_quality', {
        action: 'js_error',
        metric: 'js_error',
        value_bucket: 'error',
        error_source: sourceBucket,
        error_signature: sourceBucket + '_' + shortHash(message)
      });
    });
    window.addEventListener('unhandledrejection', function (event) {
      var reason = event && event.reason;
      var message = cleanText((reason && (reason.message || reason.name)) || reason || 'promise_rejection', 120);
      sendEvent('hermes_ux_quality', {
        action: 'js_error',
        metric: 'js_error',
        value_bucket: 'error',
        error_source: 'promise',
        error_signature: 'promise_' + shortHash(message)
      });
    });
  }

  function handleBeforeUnload() {
    var engagedMs = Date.now() - pageStartedAt;
    if (engagedMs >= 5000) {
      sendEvent('hermes_section_view', {
        section_id: 'page_total',
        section_name: 'page_total',
        visible_ms: engagedMs
      });
    }
  }

  function init() {
    if (config.enabled === false) return;
    if (typeof window.gtag !== 'function' && !Array.isArray(window.dataLayer)) return;
    reportMobileViewport();
    document.addEventListener('click', handleClick, true);
    document.addEventListener('change', handleChange, true);
    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('touchstart', handleTouchStart, { passive: true, capture: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true, capture: true });
    observeAddToCartResult();
    observeJsErrors();
    observeSections();
    observeCtas();
    observeUxQuality();
    window.addEventListener('pagehide', handleBeforeUnload, { once: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
